import { useMemo, useState, type ReactNode } from 'react';
import { formatReportMoney, hostShareCopy, type SessionLedgerLegRead } from '@evi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity, AlertTriangle, ArrowLeft, Zap, Clock, Battery, DollarSign,
  ChevronRight, Radio, Loader2, RotateCcw, CheckCircle2, XCircle,
} from 'lucide-react';
import {
  fetchSession, fetchSessionFrames, fetchSessionPayments, formatCents, refundSession,
  type SessionDetail, type SessionPayments,
} from '@/lib/api';
import { pairFrames, PairedFrameList } from '@/components/ocpp/PairedFrames';
import { DriverCell } from '@/components/sessions/DriverCell';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useAuthStore } from '@/store/auth';
import { isSiteCallerUser } from '@/lib/siteDashboard';
import { SESSION_PAYMENT_LABELS, sessionPaymentState } from '@/lib/sessionPayment';
import { rateDollarsLabel } from '@/lib/siteElectricityInput';
import { recentMeterPower, livePowerText, meterPowerText } from '@/lib/sessionPower';
import { useSessionClock } from '@/hooks/useSessionClock';
import { whatHappened } from '@/lib/sessionOutcome';
import styles from './SessionDetailPage.module.css';

interface SessionDetailPageProps {
  sessionId: string;
  onNavigate: (path: string) => void;
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  completed: 'Completed',
  failed: 'Failed',
  stopped_by_user: 'Stopped (User)',
  stopped_by_system: 'Stopped (System)',
};

function durationLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/**
 * Admin session detail.
 *
 * React Query backs the data layer. Live signals:
 *
 *   • Session summary + meter readings and payments: refetched by
 *     session and payout events (LiveUpdatesProvider), so every meter
 *     sample, status change and settlement lands without a poll.
 *     While idle fees accrue the summary still polls every 5s (the
 *     fee grows with the clock, not with a write).
 *   • OCPP frame timeline: polled every 5s while active, only when the
 *     "Frame timeline" section is expanded. No live event covers
 *     OCPP frames.
 *
 * `refetchIntervalInBackground: false` pauses the remaining polls when
 * the tab is hidden.
 */
export function SessionDetailPage({ sessionId, onNavigate }: SessionDetailPageProps) {
  const [framesOpen, setFramesOpen] = useState(false);
  // A Site persona reaches this page from a charger's "Open session" link
  // or a typed URL. The Api refuses a Site the frame timeline (403) and
  // reads it only the site's side of the session: no payout, refund, RFID
  // tag, driver or Stripe ids (lib/sessionDriverView.ts SITE_SESSION_FIELDS).
  // Those rows are not rendered for one, rather than rendered empty, and
  // stay hidden if an older Api still sends them. Staff are unchanged.
  const isSite = useAuthStore((s) => isSiteCallerUser(s.user));

  // Live (28 Oct 2026): session events (status, energy, cost, payment and
  // idle state) refetch it through LiveUpdatesProvider, so an active
  // session no longer polls. The idle arm keeps its 5s poll: the idle fee
  // grows with the clock between writes, and no event marks a minute.
  const sessionQuery = useQuery<SessionDetail>({
    queryKey: ['session', sessionId],
    queryFn: () => fetchSession(sessionId),
    refetchInterval: (q) => (q.state.data?.idle_view?.cable_connected
      || q.state.data?.idle_view?.settlement_pending ? 5_000 : false),
    refetchIntervalInBackground: false,
    staleTime: 2_500,
  });

  const session = sessionQuery.data;
  const isActive = session?.status === 'active';
  useSessionClock(isActive);
  const powerRows = useMemo(() => recentMeterPower(session?.meter_values, 50), [session?.meter_values]);
  // Payments: payout and session events refetch it; no poll.
  const paymentsQuery = useQuery({
    queryKey: ['session', sessionId, 'payments'],
    queryFn: () => fetchSessionPayments(sessionId),
    enabled: session?.can_read_payments === true && !isSite,
    staleTime: 15_000,
  });

  // Detail-page recents: empty label until data resolves, then
  // a charger-identity-prefixed label (or sliced sessionId fallback
  // when charger_identity is missing). Hook re-fires on label change.
  useRecentRoute(
    session
      ? `${session.charger_identity ?? sessionId.slice(0, 8)} (Session)`
      : ''
  );

  const framesQuery = useQuery({
    queryKey: ['session', sessionId, 'frames'],
    queryFn: () => fetchSessionFrames(sessionId, { limit: 200 }),
    // Only fetch when the timeline is expanded. Never for a Site (a 403).
    // Still polled while active: OCPP frames come from the OCPP service,
    // whose frame bus is protected, and no live event covers them.
    enabled: framesOpen && Boolean(session) && !isSite,
    refetchInterval: isActive ? 5_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 2_500,
  });

  const pairs = useMemo(
    () => (framesQuery.data ? pairFrames(framesQuery.data.data) : []),
    [framesQuery.data],
  );

  if (sessionQuery.isLoading && !session) {
    return <div className={styles.page}><p className={styles.loading}>Loading…</p></div>;
  }
  if (sessionQuery.isError || !session) {
    const msg = sessionQuery.error instanceof Error
      ? sessionQuery.error.message
      : 'Session not found';
    return (
      <div className={styles.page}>
        <div className={styles.error}>{msg}</div>
        <button className={styles.backBtn} onClick={() => onNavigate('/sessions')}>
          <ArrowLeft size={16} /> Back to Sessions
        </button>
      </div>
    );
  }

  const cur = session.cost?.currency ?? 'USD';
  // The same Payment label the Sessions list shows (lib/sessionPayment).
  const paymentState = sessionPaymentState(session);

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => onNavigate('/sessions')}>
        <ArrowLeft size={16} /> Back to Sessions
      </button>

      <div className={styles.header}>
        <div className={styles.headerIcon}><Activity size={18} /></div>
        <div style={{ flex: 1 }}>
          <h1 className={styles.title}>Session #{session.transaction_id}</h1>
          <p className={styles.subtitle}>
            {session.charger_label} ({session.charger_identity}) · {session.location_name}
          </p>
        </div>
        <span className={`${styles.statusBadge} ${styles[`status_${session.status}`] ?? ''}`}>
          {isActive && <span className={styles.liveDot} aria-hidden="true" />}
          {STATUS_LABELS[session.status] ?? session.status}
        </span>
      </div>

      {/* Summary cards */}
      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <Battery size={16} className={styles.summaryIcon} />
          <div>
            <p className={styles.summaryLabel}>Energy</p>
            <p className={styles.summaryValue}>{session.energy_kwh.toFixed(2)} kWh</p>
          </div>
        </div>
        <div className={styles.summaryCard}>
          <Clock size={16} className={styles.summaryIcon} />
          <div>
            <p className={styles.summaryLabel}>Duration</p>
            <p className={styles.summaryValue}>{durationLabel(session.duration_minutes)}</p>
          </div>
        </div>
        <div className={styles.summaryCard}>
          <Zap size={16} className={styles.summaryIcon} />
          <div>
            <p className={styles.summaryLabel}>{isActive ? 'Power' : 'Peak power'}</p>
            <p className={`${styles.summaryValue} ${!isActive && session.peak_power_kw == null ? styles.missingValue : ''}`}>{isActive ? livePowerText(powerRows[0])
              : session.peak_power_kw == null ? 'Not recorded' : `${session.peak_power_kw.toFixed(2)} kW`}</p>
          </div>
        </div>
        <div className={styles.summaryCard}>
          <DollarSign size={16} className={styles.summaryIcon} />
          <div>
            <p className={styles.summaryLabel}>Total</p>
            <p className={styles.summaryValue}>{formatCents(session.cost?.total ?? 0, cur)}</p>
          </div>
        </div>
      </div>

      {/* Session success: the cause the classifier sweep gave this session or
          start, its evidence, its fix and who it waits on. A Site reads the
          summary (driver-side causes as one); a session not yet classified
          shows nothing. */}
      <WhatHappened session={session} />

      {session.idle_evidence ? <IdleEvidence session={session} currency={cur} /> : null}

      {session.can_read_payments === true && !isSite && (
        <section className={styles.section}>
          <div className={styles.paymentHeader}>
            <h2 className={styles.sectionTitle}>Stripe payments</h2>
            <button className={styles.backBtn} disabled={paymentsQuery.isFetching} onClick={() => void paymentsQuery.refetch()}>
              <RotateCcw size={14} /> {paymentsQuery.isFetching ? 'Refreshing…' : 'Refresh payments'}
            </button>
          </div>
          {paymentsQuery.isPending && <p className={styles.empty}>Loading payment evidence…</p>}
          {paymentsQuery.isError && <p role="alert" className={styles.error}>Could not load payment evidence. Try Refresh payments.</p>}
          {paymentsQuery.data && <PaymentEvidence data={paymentsQuery.data} />}
        </section>
      )}

      <div className={styles.twoCol}>
        {/* Cost breakdown */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Cost breakdown</h2>
          <div className={styles.breakdownTable}>
            <div className={styles.breakdownRow}>
              <span>Energy cost</span>
              <span className={styles.amount}>{formatCents(session.cost?.energy_cost ?? 0, cur)}</span>
            </div>
            <div className={styles.breakdownRow}>
              <span>Idle / time fee</span>
              <span className={styles.amount}>{formatCents(session.cost?.time_cost ?? 0, cur)}</span>
            </div>
            {/* A pinned session (connection_fee_at_start_cents is a
                number, 0 included) bills EVI's connection fee in
                cost.flat_fee; a legacy one bills the tariff's old
                session fee there, which was the operator's. The label
                follows the pin so neither is called the other. */}
            <div className={styles.breakdownRow}>
              <span>
                {typeof session.connection_fee_at_start_cents === 'number'
                  ? 'Connection fee (EVI)'
                  : 'Session fee'}
              </span>
              <span className={styles.amount}>{formatCents(session.cost?.flat_fee ?? 0, cur)}</span>
            </div>
            <div className={styles.breakdownRow}>
              <span>Tax</span>
              <span className={styles.amount}>{formatCents(session.cost?.tax ?? 0, cur)}</span>
            </div>
            <div className={`${styles.breakdownRow} ${styles.totalRow}`}>
              <span>Total</span>
              <span className={styles.amount}>{formatCents(session.cost?.total ?? 0, cur)}</span>
            </div>
          </div>

          {/* Collect and hold (2026-09-27). Settlement refused the split
              because the Site's electricity reimbursement exceeded what
              the driver paid for energy: the driver was charged in full,
              the whole charge sits on EVI's balance, and a human pays the
              host and Site later. Its payout reads $0 to the owner, so it
              REPLACES the under-cover advisory below, whose "rounds to
              zero" explanation would be wrong for it. */}
          {!isSite && session.payout_hold ? (
            <div
              role="note"
              style={{
                marginTop: 12,
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                background: 'color-mix(in srgb, var(--evi-warn, #f59e0b) 10%, transparent)',
                border: '1px solid color-mix(in srgb, var(--evi-warn, #f59e0b) 35%, transparent)',
                borderRadius: 6,
                fontSize: 12,
                lineHeight: 1.5,
                color: 'var(--evi-warn, #f59e0b)',
              }}
            >
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                <strong>Payout held — Site electricity exceeded what the driver paid for charging.</strong>{' '}
                Site electricity{' '}
                <span className={styles.amount}>{formatCents(session.payout_hold.electricity_cost_cents, cur)}</span>
                {' '}vs energy revenue{' '}
                <span className={styles.amount}>{formatCents(session.payout_hold.energy_revenue_cents, cur)}</span>.
                The driver was charged in full and the whole charge is held on
                EVI&apos;s balance; the host and Site are paid manually once the
                rate is sorted.
                {session.payout_hold.held_at && (
                  <> Held {new Date(session.payout_hold.held_at).toLocaleString()}.</>
                )}
              </span>
            </div>
          ) : null}

          {/* Item 4b under-cover warning — variant-audit LOW finding.
              Renders when the operator nets $0 on a non-free session.
              Most common causes: short session on per-hour tariff
              (Stripe minimum dominates), or building rate close to
              customer rate. Links to the audit doc's narrative. Not on a
              held session: the notice above explains its $0. */}
          {!isSite
            && !session.payout_hold
            && session.payout_summary
            && session.payout_summary.owner_payout_cents === 0
            && session.payout_summary.gross_amount_cents > 0 && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                background: 'color-mix(in srgb, var(--evi-warn, #f59e0b) 10%, transparent)',
                border: '1px solid color-mix(in srgb, var(--evi-warn, #f59e0b) 35%, transparent)',
                borderRadius: 6,
                fontSize: 12,
                lineHeight: 1.5,
                color: 'var(--evi-warn, #f59e0b)',
              }}
            >
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Operator payout is $0 for this session. Typically happens when
                customer charge minus building reimbursement minus Stripe fees
                rounds to or below zero. Common causes: short session on per-hour
                tariff, or building rate close to customer rate. See{' '}
                <code>docs/billing/variant-audit.md</code> (LOW finding section).
              </span>
            </div>
          )}

          {/* Phase 5 multi-window TOU breakdown. Renders below the
              cost-breakdown table when the session crossed window
              boundaries (cost_segments.length > 1). Helps operators
              audit per-segment reimbursement when a guest question
              comes in or a payout looks off. Single-segment sessions
              skip this entirely. */}
          {session.cost_segments && session.cost_segments.length > 1 && (
            <SegmentBreakdownTable segments={session.cost_segments} currency={cur} />
          )}

          <div className={styles.metaList}>
            {/* Who started it, linked back to the driver profile (item 9,
                2026-10-23). The Api sends `driver` only to a viewer who
                can open driver profiles; nobody else sees this row. */}
            {session.driver && (
              <div><span className={styles.metaLabel}>Driver</span><DriverCell row={session} onNavigate={onNavigate} /></div>
            )}
            <div><span className={styles.metaLabel}>Started</span><span>{new Date(session.started_at).toLocaleString()}</span></div>
            {session.ended_at && (
              <div><span className={styles.metaLabel}>Ended</span><span>{new Date(session.ended_at).toLocaleString()}</span></div>
            )}
            {/* Not for a Site: its read has no payment facts to label. */}
            {!isSite && paymentState !== 'live' && (
              <div><span className={styles.metaLabel}>Payment</span><span>{SESSION_PAYMENT_LABELS[paymentState]}</span></div>
            )}
            {!isSite && (
              <div><span className={styles.metaLabel}>RFID Tag</span><span className={styles.mono}>{session.rfid_tag ?? '—'}</span></div>
            )}
            <div><span className={styles.metaLabel}>Meter start</span><span className={styles.mono}>{session.meter_start} Wh</span></div>
            {session.meter_stop !== undefined && (
              <div><span className={styles.metaLabel}>Meter stop</span><span className={styles.mono}>{session.meter_stop} Wh</span></div>
            )}
            {!isSite && session.can_read_payments !== true && (
              <div>
                <span className={styles.metaLabel}>Stripe payment</span>
                <span className={styles.mono}>{session.stripe_payment_intent_id ?? '—'}</span>
              </div>
            )}
          </div>

          {/* Refund control + status (HIGH 4.1 / 7.1). Renders only when
              there's a payout to refund — gated server-side too. Not for a
              Site: refund_state is stripped from its read and the refund
              route refuses it. */}
          {!isSite && <RefundControl session={session} />}
        </div>

        {/* Meter values timeline (expanded by default — live telemetry is the
            first thing operators want to see). */}
        <details className={styles.section} open>
          <summary className={styles.sectionSummary}>
            <ChevronRight size={14} className={styles.sectionChev} />
            <h2 className={styles.sectionTitle}>
              Meter readings ({session.meter_values.length})
              {isActive && <span className={styles.liveBadge}>LIVE</span>}
            </h2>
          </summary>
          {session.meter_values.length === 0 ? (
            <p className={styles.empty}>
              {isActive
                ? 'Waiting for first MeterValues frame from the charger…'
                : 'No meter readings recorded.'}
            </p>
          ) : (
            <div className={styles.meterList}>
              {powerRows.map((row, i) => (
                <div key={row.at + '/' + i} className={styles.meterRow}>
                  <span className={styles.meterTime}>{new Date(row.reading.timestamp).toLocaleTimeString()}</span>
                  <span className={styles.meterValue}>{row.reading.energy_kwh.toFixed(3)} kWh</span>
                  <span className={styles.meterPower}>{meterPowerText(row, 2)}</span>
                  {row.reading.soc_percent !== undefined && <span className={styles.meterMeta}>{row.reading.soc_percent.toFixed(0)}%</span>}
                  {row.reading.voltage !== undefined && <span className={styles.meterMeta}>{row.reading.voltage.toFixed(0)}V</span>}
                </div>
              ))}
            </div>
          )}
          {powerRows.some((row) => row.estimated) && (
            <p className={styles.empty}>Estimated power is the average between consecutive energy readings.</p>
          )}
          {session.meter_values.length > 50 && (
            <p className={styles.empty}>Showing latest 50 of {session.meter_values.length}</p>
          )}
        </details>
      </div>

      {/* OCPP frame timeline — collapsed by default. Deferred fetch:
          the frames query is disabled until this section is opened.
          Not for a Site: GET /sessions/:id/frames answers them 403 (the
          frames carry every driver's idTag). */}
      {!isSite && (
      <details
        className={styles.section}
        onToggle={(e) => setFramesOpen((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary className={styles.sectionSummary}>
          <ChevronRight size={14} className={styles.sectionChev} />
          <h2 className={styles.sectionTitle}>
            <Radio size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            OCPP frame timeline
            {pairs.length > 0 && <span className={styles.frameCount}>({pairs.length})</span>}
            {isActive && framesOpen && <span className={styles.liveBadge}>LIVE</span>}
          </h2>
        </summary>
        <p className={styles.framesHint}>
          Every request/response pair the charger exchanged during this session's
          time window (± 30s). Paired by <code>message_id</code>.
        </p>
        {framesQuery.isLoading && (
          <p className={styles.empty}>
            <Loader2 size={14} className={styles.spin} /> Loading frames…
          </p>
        )}
        {framesQuery.isError && (
          <div className={styles.error}>
            {framesQuery.error instanceof Error
              ? framesQuery.error.message
              : 'Failed to load frames'}
          </div>
        )}
        {!framesQuery.isLoading && !framesQuery.isError && (
          <PairedFrameList
            pairs={pairs}
            emptyLabel={
              isActive
                ? 'No frames yet in this session window. Waiting for live traffic…'
                : 'No OCPP frames were logged during this session.'
            }
            maxHeight="65dvh"
          />
        )}
      </details>
      )}
    </div>
  );
}

function utcTime(value: string | null | undefined): ReactNode {
  if (!value) return 'Not reported';
  return <time dateTime={value} title={value}>{new Date(value).toLocaleString(undefined,
    { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'long' })}</time>;
}

export function WhatHappened({ session }: { session: SessionDetail }) {
  const view = whatHappened(session.outcome ?? null, session.outcome_summary ?? null);
  if (!view) return null;
  const tone = view.tone === 'bad' ? styles.outcomeBad : view.tone === 'muted' ? styles.outcomeMuted : styles.outcomeOk;
  return <section className={styles.section} aria-label="What happened">
    <div className={styles.paymentHeader}>
      <h2 className={styles.sectionTitle}>What happened</h2>
      <span className={`${styles.outcomeBadge} ${tone}`}>{view.title}</span>
    </div>
    {view.sentence && <p className={styles.outcomeSentence}>{view.sentence}</p>}
    {view.facts.length > 0 && <dl className={styles.evidenceGrid}>
      {view.facts.map((f) => <div key={f.label}><dt>{f.label}</dt><dd>{f.value}</dd></div>)}
    </dl>}
    {(view.fix || view.waitingOn) && <dl className={styles.evidenceGrid}>
      {view.fix && <div><dt>Fix</dt><dd><strong>{view.fix}</strong></dd></div>}
      {view.waitingOn && <div><dt>Waiting on</dt><dd><strong>{view.waitingOn}</strong></dd></div>}
    </dl>}
    {view.flags.map((f) => <p key={f} role="note" className={styles.framesHint}>{f}</p>)}
  </section>;
}

export function IdleEvidence({ session, currency }: { session: SessionDetail; currency: string }) {
  const e = session.idle_evidence;
  if (!e) return null;
  const money = (cents: number | null) => formatReportMoney(cents, e.currency ?? currency);
  return <section className={styles.section}>
    <h2 className={styles.sectionTitle}>Idle evidence (UTC)</h2>
    <dl className={styles.evidenceGrid}>
      <div><dt>Charging ended</dt><dd><strong>{utcTime(e.charging_ended_at)}</strong></dd></div>
      <div><dt>Grace ended</dt><dd><strong>{utcTime(e.grace_ends_at)}</strong></dd></div>
      <div><dt>Physical unplug</dt><dd><strong>{e.cable_connected ? 'Still connected' : utcTime(e.physical_unplugged_at)}</strong></dd></div>
      <div><dt>Idle charge finalized</dt><dd><strong>{utcTime(e.financial_finalized_at)}</strong></dd></div>
      <div><dt>Billed idle minutes</dt><dd><strong>{e.billable_minutes == null ? 'Not recorded' : `${e.billable_minutes} min`}</strong></dd></div>
      <div><dt>Pinned idle rate</dt><dd><strong>{money(e.rate_cents)}/min</strong></dd></div>
      <div><dt>Pinned grace</dt><dd><strong>{e.grace_minutes == null ? 'Not recorded' : `${e.grace_minutes} min`}</strong></dd></div>
      <div><dt>Pinned billed-time cap</dt><dd><strong>{e.billed_cap_minutes == null ? 'Not recorded' : `${e.billed_cap_minutes} min`}</strong></dd></div>
      <div><dt>Pinned host maximum</dt><dd>{e.host_max_charge_cents == null ? 'No additional maximum'
        : <strong>{money(e.host_max_charge_cents)}</strong>}</dd></div>
      <div><dt>Effective maximum</dt><dd><strong>{money(e.effective_max_charge_cents)}</strong></dd></div>
      <div><dt>Cap reached</dt><dd>{e.cap_reached == null ? 'Not recorded' : e.cap_reached ? 'Yes' : 'No'}</dd></div>
      <div><dt>Cap reason</dt><dd>{e.cap_reason === 'host_maximum' ? 'Host maximum' : e.cap_reason === 'billed_time_limit' ? 'Billed-time limit' : 'No cap reached'}</dd></div>
      <div><dt>Idle fee</dt><dd><strong>{money(e.idle_fee_cents)}</strong></dd></div>
      <div><dt>Billing evidence confirmed through</dt><dd><strong>{utcTime(e.evidence_until)}</strong></dd></div>
    </dl>
    <p className={styles.framesHint}>Charge finalization can occur at the cap before unplugging. Physical unplug is a separate recorded event.</p>
  </section>;
}

export function PaymentEvidence({ data }: { data: SessionPayments }) {
  const labels = { hold: 'Hold / capture', idle: 'Follow-on idle charge', overage: 'Overage / shortfall', session: 'Session payment' };
  return <>
    {!data.complete && <p role="alert" className={styles.error}>Some Stripe payment evidence could not be verified. Refresh to try again.</p>}
    {data.complete && data.payments.length === 0 && <p className={styles.empty}>No Stripe payments recorded for this session.</p>}
    <div className={styles.paymentList}>
      {data.payments.map(payment => {
        const money = (amount: number | null) => amount == null || !payment.currency ? 'Not verified' : formatCents(amount, payment.currency);
        return <article key={payment.id} className={styles.paymentCard}>
          <div className={styles.paymentHeader}><strong>{labels[payment.kind]}</strong><span>{payment.status.replaceAll('_', ' ')}</span></div>
          <code className={styles.mono}>{payment.id}</code>
          <dl className={styles.evidenceGrid}>
            <div><dt>Authorized / requested</dt><dd><strong>{money(payment.requested_cents)}</strong></dd></div>
            <div><dt>Received</dt><dd><strong>{money(payment.received_cents)}</strong></dd></div>
            <div><dt>Still capturable</dt><dd><strong>{money(payment.capturable_cents)}</strong></dd></div>
            <div><dt>Created (UTC)</dt><dd>{utcTime(payment.created_at)}</dd></div>
          </dl>
          {payment.charges.map(charge => <div key={charge.id} className={styles.chargeRow}>
            <code className={styles.mono}>{charge.id}</code><span>{charge.status}</span>
            <span>Captured: <strong>{money(charge.captured_cents)}</strong></span>
            <span>Released / refunded: <strong>{money(charge.refunded_cents)}</strong></span>
          </div>)}
        </article>;
      })}
    </div>
    <LedgerLegEvidence legs={data.legs ?? []} />
    <p className={styles.framesHint}>Authorization is not a payment. Received and captured amounts show the money Stripe collected; released amounts are not extra charges.</p>
    <p className={styles.framesHint}>Checked {utcTime(data.checked_at)}.</p>
  </>;
}

export function LedgerLegEvidence({ legs }: { legs: SessionLedgerLegRead[] }) {
  const siteCopy=hostShareCopy(typeof navigator==='undefined'?'en':navigator.language);
  return <div className={styles.paymentList}>
    <h3 className={styles.sectionTitle}>Per-leg reconciliation</h3>
    {!legs.length && <p className={styles.framesHint}>Per-leg allocations were not recorded for this session. Historical shares are not recalculated.</p>}
    {legs.map((leg, i) => {
      const money = (amount: number | null | undefined) => formatReportMoney(amount, leg.currency);
      const fee = leg.processor_estimate;
      const cash = leg.actual_capture;
      return <article key={`${leg.kind}:${i}`} className={styles.paymentCard}>
        <h4>{leg.kind === 'capture' ? 'Hold capture' : leg.kind === 'idle' ? 'Follow-on idle' : leg.kind.replaceAll('_', ' ')}</h4>
        {leg.payment_intent_id && <code className={styles.mono}>{leg.payment_intent_id}</code>}
        {leg.review_hold && <p className={styles.framesHint}>Allocation held for review. This is not EVI earnings.</p>}
        <dl className={styles.evidenceGrid}>
          <div><dt>Gross (recorded allocation)</dt><dd><strong>{money(leg.gross_cents)}</strong></dd></div>
          <div><dt>Processing (estimate)</dt><dd><strong>{money(fee?.processing_cents)}</strong></dd></div>
          <div><dt>International card cost (estimate)</dt><dd><strong>{money(fee?.international_cents)}</strong></dd></div>
          <div><dt>Conversion cost (estimate)</dt><dd><strong>{money(fee?.conversion_cents)}</strong></dd></div>
          <div><dt>Fixed charge cost (estimate)</dt><dd><strong>{money(fee?.fixed_cents)}</strong></dd></div>
          <div><dt>Total processor cost (estimate)</dt><dd><strong>{money(fee?.total_cents)}</strong></dd></div>
          <div><dt>Host entitlement (ledger)</dt><dd><strong>{money(leg.host_entitlement_cents)}</strong></dd></div>
          {leg.site_fee_cents!=null&&<>
            <div><dt>{siteCopy.standard}</dt><dd><strong>{money(leg.standard_gross_cents??0)}</strong></dd></div>
            <div><dt>{siteCopy.fee}</dt><dd><strong>{money(leg.site_fee_cents)}</strong></dd></div>
            <div><dt>{siteCopy.processing} (estimate)</dt><dd><strong>{money(leg.site_processing_cents??0)}</strong></dd></div>
            <div><dt>{siteCopy.site} (ledger)</dt><dd><strong>{money(leg.site_share_cents??0)}</strong></dd></div>
          </>}
          <div><dt>Electricity reimbursement (ledger)</dt><dd><strong>{money(leg.electricity_entitlement_cents)}</strong></dd></div>
          <div><dt>Resident entitlement (ledger)</dt><dd><strong>{money(leg.resident_entitlement_cents)}</strong></dd></div>
          <div><dt>EVI share after estimated costs</dt><dd><strong>{money(leg.evi_share_cents)}</strong></dd></div>
          <div><dt>Application fee (recorded allocation)</dt><dd><strong>{money(leg.application_fee_cents)}</strong></dd></div>
          <div><dt>Connect net host cash (actual capture receipt)</dt><dd><strong>{formatReportMoney(cash?.host_net_cents, cash?.currency)}</strong></dd></div>
          <div><dt>Application fee cash (actual capture receipt)</dt><dd><strong>{formatReportMoney(cash?.application_fee_cents, cash?.currency)}</strong></dd></div>
          <div><dt>Capture exchange rate</dt><dd>{cash ? `${cash.exchange_rate} ${cash.currency ?? 'unknown'}/${leg.currency ?? 'unknown'}` : 'Not recorded'}</dd></div>
        </dl>
        {fee?.quote && <p className={styles.framesHint}>Processor estimate quote: {fee.quote.cad_per_usd} CAD/USD, {fee.quote.as_of}.</p>}
        {cash?.charge_id && <code className={styles.mono}>{cash.charge_id}</code>}
        {cash?.transfer_id && <code className={styles.mono}>{cash.transfer_id}</code>}
      </article>;
    })}
    <p className={styles.framesHint}>Read only. Estimates are not actual Stripe fees. Net host cash is the recorded capture receipt after the application-fee debit, before any later reversal; it is not the gross destination transfer or a bank payout.</p>
  </div>;
}

// ── Refund control (HIGH 4.1 / 7.1) ──────────────────────────────
//
// Inline rather than its own module — it's tightly coupled to the
// Session detail page and the styles match the under-cover-warning
// callout pattern already in this file (inline-styled, no module
// .css explosion for a one-off control).
//
// Three states the section renders:
//
//   • No refund_state OR no payment_intent → renders nothing.
//     Admin can't refund a session that didn't take money.
//
//   • refund_state.refund_status === null → "Refund" button.
//     Eligible payouts are status='paid' or 'transferred' AND
//     the session isn't still active. The server route enforces
//     this strictly (404/422/409 etc.) — the FE is just a faster
//     gate to avoid obviously-doomed POSTs.
//
//   • refund_state.refund_status set → status section showing
//     pending / completed / failed_partial state with the
//     refund_id, amount, initiator, and timestamp.
//
// Auth: only super_admin + tenant_admin see the button. Drivers
// hitting this page (own-session view) see nothing — the server
// will reject them anyway, but no point teasing the UI.

interface RefundControlProps {
  session: SessionDetail;
}

function RefundControl({ session }: RefundControlProps) {
  const queryClient = useQueryClient();
  const userRole = useAuthStore((s) => s.user?.role);
  const isAdmin = userRole === 'super_admin' || userRole === 'tenant_admin';

  const [modalOpen, setModalOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const refundMutation = useMutation({
    mutationFn: (r: string) => refundSession(String(session._id), r),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['session', String(session._id)] });
      setModalOpen(false);
      setReason('');
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err instanceof Error
          ? err.message
          : 'Refund failed — try again or check audit log.'
      );
    },
  });

  const refundState = session.refund_state;
  // No payout / no card-on-file → nothing to do here.
  if (!refundState || !refundState.has_payment_intent) return null;

  const isActive = session.status === 'active';
  const payoutPaid =
    refundState.payout_status === 'paid'
    || refundState.payout_status === 'transferred';
  const alreadyRefundedOrPending =
    refundState.refund_status === 'pending'
    || refundState.refund_status === 'completed'
    || refundState.refund_status === 'failed_partial';

  const canRefund = isAdmin && !isActive && payoutPaid && !alreadyRefundedOrPending;

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--color-border, var(--evi-border))' }}>
      {/* Status block — renders whenever refund_status is set, regardless of role */}
      {refundState.refund_status && (
        <RefundStatusBlock state={refundState} />
      )}

      {/* Action button — admins only, eligible state only */}
      {canRefund && (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            background: 'transparent',
            border: '1px solid var(--evi-warn, #f59e0b)',
            color: 'var(--evi-warn, #f59e0b)',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <RotateCcw size={14} /> Refund session
        </button>
      )}

      {/* Modal */}
      {modalOpen && (
        <RefundModal
          gross={session.payout_summary?.gross_amount_cents ?? 0}
          currency={session.cost?.currency ?? 'USD'}
          reason={reason}
          setReason={setReason}
          submitting={refundMutation.isPending}
          submitError={submitError}
          onCancel={() => {
            if (!refundMutation.isPending) {
              setModalOpen(false);
              setSubmitError(null);
            }
          }}
          onSubmit={() => {
            const trimmed = reason.trim();
            if (!trimmed) {
              setSubmitError('Please enter a reason for the refund.');
              return;
            }
            setSubmitError(null);
            refundMutation.mutate(trimmed);
          }}
        />
      )}
    </div>
  );
}

function RefundStatusBlock({
  state,
}: {
  state: NonNullable<SessionDetail['refund_state']>;
}) {
  const status = state.refund_status;
  const palette: Record<string, { fg: string; bg: string; icon: ReactNode; label: string }> = {
    pending: {
      fg: 'var(--evi-warn, #f59e0b)',
      bg: 'color-mix(in srgb, var(--evi-warn, #f59e0b) 10%, transparent)',
      icon: <Loader2 size={14} className={styles.spin} />,
      label: 'Refund pending',
    },
    completed: {
      fg: 'var(--evi-success, #10b981)',
      bg: 'color-mix(in srgb, var(--evi-success, #10b981) 10%, transparent)',
      icon: <CheckCircle2 size={14} />,
      label: 'Refunded',
    },
    failed_partial: {
      fg: 'var(--evi-error, #ef4444)',
      bg: 'color-mix(in srgb, var(--evi-error, #ef4444) 10%, transparent)',
      icon: <XCircle size={14} />,
      label: 'Refunded — reversal failed',
    },
  };
  const p = status ? palette[status] : null;
  if (!p) return null;

  const refundedAt = state.refunded_at
    ? new Date(state.refunded_at).toLocaleString()
    : null;

  return (
    <div
      style={{
        marginBottom: 12,
        padding: '10px 12px',
        background: p.bg,
        border: `1px solid color-mix(in srgb, ${p.fg} 35%, transparent)`,
        borderRadius: 6,
        color: p.fg,
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
        {p.icon} {p.label}
      </div>
      <div style={{ marginTop: 6, color: 'var(--color-tx)' }}>
        {state.refund_amount_cents != null && (
          <div>
            <strong>Amount:</strong>{' '}
            {formatCents(state.refund_amount_cents, 'USD')}
          </div>
        )}
        {state.refund_initiated_by && (
          <div>
            <strong>Initiated by:</strong>{' '}
            {state.refund_initiated_by === 'admin' ? 'Admin (EVI panel)' : 'Stripe dashboard'}
          </div>
        )}
        {state.refund_reason && (
          <div>
            <strong>Reason:</strong> {state.refund_reason}
          </div>
        )}
        {state.refund_id && (
          <div>
            <strong>Stripe refund:</strong>{' '}
            <code style={{ fontSize: 11 }}>{state.refund_id}</code>
          </div>
        )}
        {refundedAt && (
          <div>
            <strong>At:</strong> {refundedAt}
          </div>
        )}
        {status === 'failed_partial' && (
          <div style={{ marginTop: 6, fontStyle: 'italic' }}>
            Resident-markup transfer reversal failed. Manual reconciliation
            required — see audit log for the transfer id and error details.
          </div>
        )}
      </div>
    </div>
  );
}

function RefundModal({
  gross, currency, reason, setReason, submitting, submitError, onCancel, onSubmit,
}: {
  gross: number;
  currency: string;
  reason: string;
  setReason: (r: string) => void;
  submitting: boolean;
  submitError: string | null;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="refund-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(440px, 100%)',
          background: 'var(--color-surface, #1a1a1a)',
          border: '1px solid var(--color-border, var(--evi-border))',
          borderRadius: 8,
          padding: 20,
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.4)',
        }}
      >
        <h2 id="refund-modal-title" style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700 }}>
          Refund this session?
        </h2>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
          Full refund of <strong>{formatCents(gross, currency)}</strong>. EVI absorbs
          the Stripe processing fee. Connect transfers (operator + resident markup if
          applicable) reverse automatically. This cannot be undone from the panel.
        </p>

        <label
          htmlFor="refund-reason"
          style={{
            display: 'block',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            color: 'var(--color-tx-m)',
            marginBottom: 6,
          }}
        >
          Reason (required) — recorded in audit log
        </label>
        <textarea
          id="refund-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          disabled={submitting}
          placeholder="e.g. Charger fault mid-session, customer reported no power"
          style={{
            width: '100%',
            padding: 8,
            fontSize: 13,
            background: 'var(--color-bg, #0a0a0a)',
            color: 'var(--color-tx)',
            border: '1px solid var(--color-border, var(--evi-border))',
            borderRadius: 6,
            resize: 'vertical',
            fontFamily: 'inherit',
          }}
        />

        {submitError && (
          <div
            style={{
              marginTop: 10,
              padding: '8px 10px',
              fontSize: 12,
              color: 'var(--evi-error, #ef4444)',
              background: 'color-mix(in srgb, var(--evi-error, #ef4444) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--evi-error, #ef4444) 35%, transparent)',
              borderRadius: 6,
            }}
          >
            {submitError}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: '8px 14px',
              background: 'transparent',
              color: 'var(--color-tx)',
              border: '1px solid var(--color-border, var(--evi-border))',
              borderRadius: 6,
              fontSize: 13,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting || !reason.trim()}
            style={{
              padding: '8px 14px',
              background: 'var(--evi-warn, #f59e0b)',
              color: '#000',
              border: '1px solid var(--evi-warn, #f59e0b)',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              cursor: submitting || !reason.trim() ? 'not-allowed' : 'pointer',
              opacity: submitting || !reason.trim() ? 0.6 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {submitting && <Loader2 size={14} className={styles.spin} />}
            {submitting ? 'Refunding…' : 'Confirm refund'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Phase 5 segment breakdown ────────────────────────────────────
//
// Renders a per-window cost breakdown below the cost-breakdown
// table on admin Session detail. Operators audit per-segment
// reimbursement when a payout looks off or a customer asks why
// a session cost what it cost.
//
// Inline-styled rather than CSS-module'd — keeps the change
// contained, follows the under-cover-warning callout pattern just
// above this in the same component.

interface AdminSegmentRow {
  start_at: string;
  end_at: string;
  duration_minutes: number;
  energy_kwh: number;
  rate_cents: number;
  rate_type: 'per_kwh' | 'per_hour';
  window_label: string;
  cost_cents: number;
}

function SegmentBreakdownTable({
  segments, currency,
}: {
  segments: AdminSegmentRow[];
  currency: string;
}) {
  return (
    <div style={{ marginTop: 16 }}>
      <h3
        style={{
          margin: '0 0 8px',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '1.4px',
          textTransform: 'uppercase',
          color: 'var(--color-tx-m)',
        }}
      >
        TOU breakdown ({segments.length} segments)
      </h3>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          border: '1px solid var(--color-border, var(--evi-border))',
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        {segments.map((seg, i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: 'var(--admin-pair-columns, minmax(120px, 1.4fr) minmax(140px, 2fr) auto)',
              gap: 12,
              padding: '8px 12px',
              alignItems: 'baseline',
              borderBottom: i < segments.length - 1
                ? '1px solid var(--color-border, var(--evi-border))'
                : 'none',
              fontSize: 12,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <span style={{ color: 'var(--color-tx)' }}>
              {formatAdminClockRange(seg.start_at, seg.end_at)}
            </span>
            <span style={{ color: 'var(--color-tx-m)' }}>
              <em style={{ fontStyle: 'italic', marginRight: 4 }}>{seg.window_label}</em>
              · {formatAdminSegmentUsage(seg)}
            </span>
            <span style={{ color: 'var(--color-tx)', fontWeight: 600 }}>
              {formatCents(seg.cost_cents, currency)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatAdminClockRange(startISO: string, endISO: string): string {
  return `${formatAdminClock(new Date(startISO))} – ${formatAdminClock(new Date(endISO))}`;
}

function formatAdminClock(d: Date): string {
  // 24h for admin (operators expect engineer-style timestamps).
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatAdminSegmentUsage(seg: AdminSegmentRow): string {
  // A flat fallback segment carries the location's rate, kept to a hundredth of a cent ("$0.4363"; Warren, 21 October
  // 2026, night: "43.63 stays 43.63").
  const rateDollars = rateDollarsLabel(seg.rate_cents);
  if (seg.rate_type === 'per_hour') {
    return `${seg.duration_minutes} min × ${rateDollars}/hour`;
  }
  return `${seg.energy_kwh.toFixed(2)} kWh × ${rateDollars}/kWh`;
}
